import React, {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import dayjs from 'dayjs';
import classNames from 'classnames';
import Icons from 'images/icons';
import Linked from 'components/linked/index';
import AprosButton from 'components/aprosButton';
import UserNameLabel from './userNameLabel';
import { RankDescription } from 'constants/rank';
import { DATE_SELECT_TEL } from '../../../constants/gtmCustomEvent';

const SCROLL_RATIO = 0.9;
const FADE_WIDTH = 180;
const MOBILE_BREAKPOINT = 640;
const DRAG_THRESHOLD = 5;

// 横スクロール可能な方向に応じて、左右のフェードマスクを動的に作成
const buildMask = (canScrollLeft, canScrollRight) => {
  if (!canScrollLeft && !canScrollRight) return undefined;
  const left = canScrollLeft
    ? `transparent 0px, black ${FADE_WIDTH}px`
    : 'black 0px';
  const right = canScrollRight
    ? `black calc(100% - ${FADE_WIDTH}px), transparent 100%`
    : 'black 100%';
  return `linear-gradient(to right, ${left}, ${right})`;
};

// 横スクロール可能な時間帯ボタン群
const TimeSlotButtons = memo(
  ({ slots = [], onClickSlot, className = '', buttonClassName = '' }) => {
    const scrollRef = useRef(null);
    const scrollStateRafRef = useRef(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [isDragStyleActive, setIsDragStyleActive] = useState(false);

    useEffect(() => {
      const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
      const handleChange = e => setIsMobile(e.matches);
      handleChange(mql);
      mql.addEventListener('change', handleChange);
      return () => mql.removeEventListener('change', handleChange);
    }, []);

    // スクロール位置に応じて左右のスクロール可能状態を更新
    const updateScrollState = useCallback(() => {
      if (!scrollStateRafRef.current) {
        scrollStateRafRef.current = requestAnimationFrame(() => {
          scrollStateRafRef.current = null;
          const element = scrollRef.current;
          if (element) {
            const { scrollLeft, scrollWidth, clientWidth } = element;
            const maxScrollLeft = scrollWidth - clientWidth;
            setCanScrollLeft(scrollLeft > 1);
            setCanScrollRight(scrollLeft < maxScrollLeft - 1);
          }
        });
      }
    }, []);

    // 「もっと見る」などで再描画されても、slot.hmの並びが同じ場合はスクロール先頭へのリセットを防ぐ
    const slotsKey = useMemo(() => slots.map(slot => slot.hm).join(','), [
      slots,
    ]);

    useLayoutEffect(() => {
      const element = scrollRef.current;
      if (element) {
        element.scrollTo({ left: 0, behavior: 'auto' });
        updateScrollState();
      }
    }, [slotsKey, updateScrollState]);

    useLayoutEffect(() => {
      const element = scrollRef.current;
      if (element) {
        const ro = new ResizeObserver(() => {
          updateScrollState();
        });
        ro.observe(element);
        return () => {
          ro.disconnect();
          if (scrollStateRafRef.current) {
            cancelAnimationFrame(scrollStateRafRef.current);
          }
        };
      }
    }, [updateScrollState]);

    const scrollByPage = direction => {
      const element = scrollRef.current;
      if (element) {
        element.scrollBy({
          left: element.clientWidth * SCROLL_RATIO * direction,
          behavior: 'smooth',
        });
      }
    };

    // 時間ボタンのクリック処理
    const handleTimeSlotClick = useCallback(
      event => {
        const button = event.target.closest('button[data-hm]');
        const slot = button && slots.find(s => s.hm === button.dataset.hm);
        slot && onClickSlot && onClickSlot(slot);
      },
      [slots, onClickSlot]
    );

    useEffect(() => {
      const element = scrollRef.current;
      if (element) {
        element.onclick = handleTimeSlotClick;
        return () => {
          element.onclick = null;
        };
      }
    }, [handleTimeSlotClick]);

    // 時間ボタン領域のマウスドラッグによる横スクロール
    const handlePointerDown = e => {
      const element = scrollRef.current;
      if (!element || e.button !== 0 || e.pointerType !== 'mouse') {
        return;
      }
      const dragStartPointerX = e.clientX;
      const dragStartScrollLeft = element.scrollLeft;

      // スクロール更新に加え、ドラッグ確定時にonclickをnullにしてポインタ捕捉を行う
      const handlePointerMove = moveEvent => {
        const movedDistanceX = moveEvent.clientX - dragStartPointerX;
        if (Math.abs(movedDistanceX) > DRAG_THRESHOLD) {
          element.onclick = null;
          setIsDragStyleActive(true);
          element.setPointerCapture(moveEvent.pointerId);
        }
        element.scrollLeft = dragStartScrollLeft - movedDistanceX;
      };
      // ドラッグ終了時の処理。clickの発生後まで少し遅らせてonclickを復元する
      const handlePointerEnd = () => {
        element.removeEventListener('pointermove', handlePointerMove);
        element.removeEventListener('pointerup', handlePointerEnd);
        setIsDragStyleActive(false);
        setTimeout(() => {
          element.onclick = handleTimeSlotClick;
        }, 0);
      };
      element.addEventListener('pointermove', handlePointerMove);
      element.addEventListener('pointerup', handlePointerEnd);
    };

    const showLeftArrow = !isMobile && canScrollLeft;
    const showRightArrow = !isMobile && canScrollRight;
    const scrollMask = isMobile
      ? undefined
      : buildMask(canScrollLeft, canScrollRight);

    return (
      <div className={classNames('flex items-center gap-2', className)}>
        {showLeftArrow && (
          <button
            type="button"
            aria-label="前の時間を表示"
            onClick={() => scrollByPage(-1)}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-navy hover:opacity-70"
          >
            <Icons.Image src={Icons.back} className="h-8 w-8" />
          </button>
        )}

        <div
          ref={scrollRef}
          onScroll={updateScrollState}
          onPointerDown={handlePointerDown}
          className={classNames(
            'flex flex-1 gap-4 sm:gap-6 overflow-x-auto p-4 -my-2 -mx-4 select-none [&::-webkit-scrollbar]:hidden',
            !isMobile && !isDragStyleActive && 'scroll-smooth',
            !isMobile && (isDragStyleActive ? 'cursor-grabbing' : 'cursor-grab')
          )}
          style={{
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
            maskImage: scrollMask,
            WebkitMaskImage: scrollMask,
          }}
        >
          {slots.map(slot => (
            <button
              key={slot.hm}
              data-hm={slot.hm}
              className={classNames(
                'min-w-[88px] shrink-0 rounded-xl bg-white px-3 flex h-11 sm:h-14 items-center justify-center text-sm text-navy drop-shadow-[2px_2px_3px_#00244529] sm:drop-shadow-[3px_3px_6px_#00244529] hover:opacity-70 max-sm:min-w-0 max-sm:w-[calc((100%-4rem)/4.8)]',
                !isMobile && 'snap-start',
                buttonClassName
              )}
            >
              {slot.hm}
            </button>
          ))}
        </div>

        {showRightArrow && (
          <button
            type="button"
            aria-label="次の時間を表示"
            onClick={() => scrollByPage(1)}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-navy hover:opacity-70"
          >
            <Icons.Image src={Icons.next} className="h-8 w-8" />
          </button>
        )}
      </div>
    );
  }
);

const AvailabilityTimeScrollContent = ({
  date,
  isSp,
  // 選択可能な日付の下限（YYYYMMDD）。未指定時は当日（従来挙動）
  minSelectableYmd = null,
  onChangeDate,
  onClickCalendar,
  dailyInfo,
  onClickTimeSlot,
  waitTimeInfo,
  onClickOneClickReserve,
  rankCode,
  shopName,
  shopTel,
  isOilWork,
  supportChargeFlag,
}) => {
  // 「他の日時を見る」アコーディオンの開閉状態
  const [isNearestOpen, setIsNearestOpen] = useState(false);
  // アコーディオン内で現在表示している日数。「もっと見る」押下で3ずつ増える
  const [nearestDisplayDays, setNearestDisplayDays] = useState(3);

  const selectedDate = dayjs(date).format('YYYYMMDD');
  const isSelectedDateToday = dayjs(date).isSame(dayjs(), 'day');
  // 選択下限（YYYYMMDD）。前日リンクの表示判定に使用
  const minYmd = minSelectableYmd || dayjs().format('YYYYMMDD');
  // 下限日に到達している場合は前日リンクを表示しない（R8）
  const isAtMinSelectableDate = selectedDate <= minYmd;
  const showOneClickReserveButton =
    isSelectedDateToday && [0, 2].includes(waitTimeInfo?.frontStatus);
  const isOneClickDisabled = waitTimeInfo?.frontStatus === 2;

  const onClickTelTracking = useCallback(() => {
    const dataLayer = (window.dataLayer = window.dataLayer || []);
    dataLayer.push({
      event: DATE_SELECT_TEL,
    });
  }, []);

  const waitTimeSgyStaHmWithDate =
    waitTimeInfo &&
    dayjs(
      `${dayjs().format('YYYYMMDD')} ${waitTimeInfo.getTime}`,
      'YYYYMMDD HH:mm'
    )
      .add(waitTimeInfo.confirmMenuWaitMinutes, 'minutes')
      .format('M月D日(ddd) HH:mm');

  // 当日表示時は「現在時刻より後」の枠だけを残し、他日は「〇」の枠をすべて表示
  const getAvailableSlots = useCallback((hmInfo = [], targetDate) => {
    const now = dayjs();
    const today = now.format('YYYYMMDD');

    return hmInfo.filter(({ hm, availability }) => {
      if (availability !== '〇') return false;
      if (targetDate !== today) return true;
      const slotDateTime = dayjs(`${targetDate}${hm}`, 'YYYYMMDDHH:mm');
      return slotDateTime.isAfter(now);
    });
  }, []);

  const selectedDateInfo = useMemo(
    () => dailyInfo?.find(d => d.date === selectedDate),
    [dailyInfo, selectedDate]
  );

  const showKeyFlag = selectedDateInfo?.showKeyFlag ?? '';

  // dailyInfo全体（選択日を含む）の中で最初にoutOfTermMessageを持つ日のindexと文言
  const { outOfTermIndex, outOfTermMessage } = useMemo(() => {
    const list = dailyInfo ?? [];
    const index = list.findIndex(d => d.outOfTermMessage);
    return {
      outOfTermIndex: index,
      outOfTermMessage: index >= 0 ? list[index].outOfTermMessage : '',
    };
  }, [dailyInfo]);

  // 選択日以降の空き枠がある日を抽出し、表示用に整形
  const selectedDateSlots = useMemo(
    () => getAvailableSlots(selectedDateInfo?.hmInfo ?? [], selectedDate),
    [selectedDateInfo, selectedDate, getAvailableSlots]
  );

  // 選択日より後で、空き枠がある日を先頭からmaxDays件だけ集める
  const getNearestDaysData = useCallback(
    (maxDays = 3) => {
      const result = [];
      for (const dayData of dailyInfo ?? []) {
        if (result.length >= maxDays) break;
        if (!dayData.date || dayData.date <= selectedDate) continue;
        if (!dayData.hmInfo) continue;
        const slots = getAvailableSlots(dayData.hmInfo, dayData.date);
        if (slots.length > 0) {
          result.push({
            date: dayData.date,
            slots,
          });
        }
      }
      return result;
    },
    [dailyInfo, selectedDate, getAvailableSlots]
  );

  // 現在の表示件数(nearestDisplayDays)より1件多く先読みし、余分に見つかれば「もっと見る」を表示する
  const nearestDaysDataExtended = useMemo(
    () => getNearestDaysData(nearestDisplayDays + 1),
    [getNearestDaysData, nearestDisplayDays]
  );
  // 実際に画面へ表示する分（nearestDisplayDays件）だけ切り出したもの
  const nearestDaysData = useMemo(
    () => nearestDaysDataExtended.slice(0, nearestDisplayDays),
    [nearestDaysDataExtended, nearestDisplayDays]
  );
  // 先読み分が表示件数より多い＝まだ表示していない空き日が残っている
  const hasMore = nearestDaysDataExtended.length > nearestDaysData.length;

  // 選択日の空き枠がない場合は「他の日時を見る」を開いた状態にする
  useEffect(() => {
    setIsNearestOpen(false);
    setNearestDisplayDays(3);
    if (selectedDateSlots.length === 0) {
      setIsNearestOpen(true);
    }
  }, [selectedDateSlots]);

  // 店舗への問い合わせ情報表示
  const renderShopContactInfo = () => (
    <div className="pt-8 text-sm">
      <div>
        ご来店順で作業受付ができる場合がございます。詳しくは電話でお問い合わせください。
      </div>
      <div className="mt-2">
        <div className="font-bold">{shopName}</div>
        <div className="mt-1 flex items-center">
          <Icons.Image src={Icons.tel} className="mt-0.5 h-4 w-4 sm:h-6 sm:w-6 shrink-0" />
          <Linked
            text={shopTel}
            href={`tel:${shopTel}`}
            isLine={isSp}
            className={`text-base font-medium ${isSp ? '' : 'pointer-events-none'}`}
            onClick={onClickTelTracking}
          />
        </div>
      </div>
      {isOilWork && supportChargeFlag !== '1' && (
        <div className="mt-2">
          <div className="font-bold">オイル交換予約サポートセンター</div>
          <div>受付時間 10:00～20:00</div>
          <div className="mt-1 flex items-center">
            <Icons.Image src={Icons.tel} className="mt-0.5 h-4 w-4 sm:h-6 sm:w-6 shrink-0" />
            <Linked
              href="tel:0120-974-643"
              className={`text-base font-medium ${isSp ? '' : 'pointer-events-none'}`}
              text="0120-974-643"
              isLine={isSp}
              onClick={onClickTelTracking}
            />
          </div>
        </div>
      )}
    </div>
  );

  // 選択日の時間帯ボタン
  const renderTimeButtons = () => {
    if (selectedDateInfo?.outOfTermMessage) {
      return (
        <p className="text-base whitespace-pre-wrap">
          {selectedDateInfo.outOfTermMessage}
        </p>
      );
    }

    if (selectedDateInfo?.closedFlag === '1') {
      return (
        <p className="text-sm my-2 whitespace-pre-wrap">
          店休日のためご予約ができません。{isSp ? '\n' : ' '}
          他の日時または店舗をご検討ください。
        </p>
      );
    }

    if (selectedDateSlots.length === 0) {
      return (
        <p className="text-sm my-2 whitespace-pre-wrap">
          この日はご予約いただける時間がありません。{'\n'}
          他の日時または店舗をご検討ください。
        </p>
      );
    }

    return (
      <>
        <p className="text-sm mt-4 mb-2">時間を選択</p>
        <TimeSlotButtons
          slots={selectedDateSlots}
          onClickSlot={slot => onClickTimeSlot(slot.hm, selectedDate)}
        />
      </>
    );
  };

  // ワンクリック予約ボタン
  const renderOneClickReserveButton = () => (
    <div
      className={classNames(
        'mt-4 flex flex-col border lg:max-w-[635px]',
        isOneClickDisabled
          ? 'border-disabled text-disabled'
          : 'border-navy-hover text-navy'
      )}
    >
      {waitTimeInfo?.confirmMenuWaitMinutes != null &&
        waitTimeInfo?.frontStatus === 0 && (
          <div
            className={classNames(
              'flex w-full flex-row items-end justify-center py-1 text-white',
              isOneClickDisabled ? 'bg-disabled' : 'bg-navy-hover'
            )}
          >
            {waitTimeInfo?.confirmMenuWaitMinutes === 0 ? (
              <label className="text-sm font-medium">
                今から作業受付が可能です。
              </label>
            ) : (
              <>
                <label className="text-sm font-medium">今なら約</label>
                <label className="mx-1 text-[22px] font-medium leading-[28px] sm:text-[32px] sm:leading-[35px]">
                  {waitTimeInfo?.confirmMenuWaitMinutes}
                </label>
                <label className="text-sm font-medium">
                  分後のご予約が可能です
                </label>
              </>
            )}
          </div>
        )}
      <div className="flex w-full flex-col justify-center bg-white px-2 pb-7 pt-2 text-center sm:pb-6 sm:pt-4">
        {waitTimeInfo?.confirmMenuWaitMinutes != null &&
        waitTimeInfo?.confirmMenuWaitMinutes !== 0 &&
        !isOneClickDisabled ? (
          <>
            <div className="flex flex-row items-end justify-center gap-x-2">
              <label className="text-sm">作業開始目安時間</label>
              <label className="text-base font-bold text-red">
                {waitTimeSgyStaHmWithDate}
              </label>
            </div>
            <div className="text-sm mt-1 sm:mt-2">この時間で予約しますか？</div>
          </>
        ) : (
          <div className="flex flex-row items-end justify-center gap-x-2">
            <label className="text-sm">
              「待ち時間予約」サービスは、現在ご利用できません。
            </label>
          </div>
        )}
        <div className="mt-6 sm:mt-5">
          <AprosButton
            comClassName="reserve"
            text="ワンクリックで予約する"
            disabled={isOneClickDisabled}
            onClick={onClickOneClickReserve}
          />
        </div>
      </div>
    </div>
  );

  // 直近の空き日アコーディオンの中身
  const renderNext3DaysAccordion = () => {
    if (!isNearestOpen) return null;

    return (
      <div className="space-y-4">
        {nearestDaysData.map(day =>
          <div key={day.date}>
            <p className="text-base font-bold">
              {dayjs(day.date).format('YYYY年M月D日(ddd)')}
            </p>
            <TimeSlotButtons
              slots={day.slots}
              className="mt-2"
              onClickSlot={slot => onClickTimeSlot(slot.hm, day.date)}
            />
          </div>
        )}
        {hasMore && (
          // まだ表示していない空き日がある場合のみ「もっと見る」を表示。
          <div className="sm:pt-8">
            <button
              className="text-sm mb-10 inline-flex items-center gap-1 hover:opacity-70"
              onClick={() => setNearestDisplayDays(prev => prev + 3)}
            >
              もっと見る
              <Icons.Image src={Icons.pulldown} className="h-6 w-6" />
            </button>
          </div>
        )}
        {/* これ以上表示できる空き日が残っていない場合（空き日が0件の場合を含む）店舗問い合わせ情報を表示し、outOfTermMessageがある場合はさらに適用期間エラー文言を表示 */}
        {!hasMore && (
          <>
            {outOfTermIndex > 0 && (
              <p className="text-sm whitespace-pre-wrap">{outOfTermMessage}</p>
            )}
            {renderShopContactInfo()}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="mt-6 sm:mt-8 lg:mt-10">
      <div className="flex h-16 items-center justify-center gap-4 border-b border-line bg-navy px-4 text-white sm:gap-6">
        {isAtMinSelectableDate ? (
          <span className="min-w-[2rem] sm:min-w-[8rem] shrink-0" />
        ) : (
          <button
            className="group inline-flex min-w-[2rem] sm:min-w-[8rem] shrink-0 justify-center whitespace-nowrap text-sm"
            onClick={() => onChangeDate(dayjs(date).subtract(1, 'day').format('YYYY/M/D'))}
          >
            <span className="inline-flex items-center border-b border-white group-hover:opacity-70">
              <Icons.Image src={Icons.linkLW} className="mt-0.5 h-5 w-5 sm:mt-1 sm:h-6 sm:w-6" />
              <span>前日</span>
            </span>
          </button>
        )}
        <button className="group" onClick={onClickCalendar}>
          <span className="text-[18px] sm:text-[24px] border-current inline-flex items-center border-b group-hover:opacity-70">
            <span className="mr-1">{dayjs(date).format('YYYY年M月D日(ddd)')}</span>
            <Icons.Image
              src={Icons.cal}
              className="h-5 w-5 sm:h-6 sm:w-6 sm:mt-1 sm:mb-0"
              style={{ filter: 'brightness(0) invert(1)' }}
            />
          </span>
        </button>
        <button
          className="group inline-flex min-w-[2rem] sm:min-w-[8rem] shrink-0 justify-center whitespace-nowrap text-sm"
          onClick={() => onChangeDate(dayjs(date).add(1, 'day').format('YYYY/M/D'))}
        >
          <span className="inline-flex items-center border-b border-white group-hover:opacity-70">
            <span>翌日</span>
            <Icons.Image
              src={Icons.linkRW}
              className="mt-0.5 h-5 w-5 sm:mt-1 sm:h-6 sm:w-6"
            />
          </span>
        </button>
      </div>

      <div className="px-4 py-4">
        {/* 上部ランクカード: 鍵帯開放前かつ当日かつ店休日でない場合に表示 */}
        {showKeyFlag === '1' && isSelectedDateToday && selectedDateInfo?.closedFlag !== '1' && (
          <>
            <UserNameLabel className="mt-4" />
            {RankDescription(rankCode)}
          </>
        )}
        {/* 時間選択エリア: 鍵帯開放前は非表示 */}
        {showKeyFlag !== '1' && renderTimeButtons()}
        {/* 待ち時間予約ボタン: 当日かつfrontStatusが0または2のときに表示 */}
        {showOneClickReserveButton && renderOneClickReserveButton()}

        <div className="mt-10">
          <button
            className="mb-6 text-sm inline-flex items-center gap-1 hover:opacity-70 sm:mb-10"
            onClick={() => {
              // 開いている状態から閉じる時は、表示件数を初期値(3件)に戻す
              isNearestOpen && setNearestDisplayDays(3);
              setIsNearestOpen(prev => !prev);
            }}
          >
            他の日時を見る
            <Icons.Image
              src={Icons.pulldown}
              className="h-6 w-6"
              style={isNearestOpen ? { transform: 'rotate(180deg)' } : undefined}
            />
          </button>

          {renderNext3DaysAccordion()}
        </div>

        {/* 下部ランクカード: 鍵帯開放後かつ当日の場合に表示 */}
        {showKeyFlag === '0' && isSelectedDateToday && (
          <>
            <UserNameLabel className="mt-8 lg:mt-10" />
            {RankDescription(rankCode)}
          </>
        )}
      </div>
    </div>
  );
};

export default AvailabilityTimeScrollContent;
